import React, { useState, useEffect } from 'react';
import { Table, Button, Icon, Modal, Header, Message, Toast, Pagination } from 'react-frontier';
import { Dropdown, Rating } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { AddWorkerModal, RateWorkerModal, InfoLog, SendWorkersSMSModal } from './index';
import { EventWorker, SetLoading } from '../SiteClasses';
import { addCommas, bindClick } from '../SiteUtil';
import { useUser } from '../hooks';
import UserAccess from '../UserAccess';
import API from '../API';

interface WorkersTableProps{
	dateId: number;
}

const PAGE_SIZE = 20;

var WorkersTable = (props: WorkersTableProps)=>{
	var { hasAccess } = useUser();
	var [workers, setWorkers] = useState<EventWorker[]>([]);
	var [loading, setLoading] = useState<boolean>(true);
	var [page, setPage] = useState<number>(0);
	var [selectedWorker, setSelectedWorker] = useState<EventWorker>(null);
	var [showCancelModal, setShowCancelModal] = useState<boolean>(false);
	var [showAddWorkerModal, setShowAddWorkerModal] = useState<boolean>(false);
	var [showRateWorkerModal, setShowRateWorkerModal] = useState<boolean>(false);
	var [showInfoLogModal, setShowInfoLogModal] = useState<boolean>(false);
	var [showSMSModal, setShowSMSModal] = useState<boolean>(false);
	var [smsSingleWorker, setSmsSingleWorker] = useState<EventWorker>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(() => {
		getWorkers();
	}, [props.dateId]);

	var getWorkers = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
		
		setLoading(true);
		API.getEventWorkers(props.dateId, PAGE_SIZE, offset).then(res=>{
			if(res.error) return Toast.error(res.message);
			setWorkers(res.data);
		}).catch(err=>{
			Toast.error('Hubo un error cargando los trabajadores (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onPageChange = (page: number)=>{
		setLoading(true);
		setPage(page);
		getWorkers(page);
	}

	var onShowCancel = (worker: EventWorker)=>{
		setSelectedWorker(worker);
		setShowCancelModal(true);
	}

	var onSendSMSToWorker = (worker: EventWorker)=>{
		setSmsSingleWorker(worker);
		setShowSMSModal(true);
	}

	var onSendSMSBulk = ()=>{
		setSmsSingleWorker(null);
		setShowSMSModal(true);
	}

	var onAddWorker = ()=>{
		setSelectedWorker(null);
		setShowAddWorkerModal(true);
	}

	var onEditWorker = (worker: EventWorker)=>{
		setSelectedWorker(worker);
		setShowAddWorkerModal(true);
	}

	var onRateWorker = (worker: EventWorker)=>{
		setSelectedWorker(worker);
		setShowRateWorkerModal(true);
	}

	var onWorkerAdded = (worker: EventWorker)=>{
		setWorkers(prev => [...prev, worker]);
		setShowAddWorkerModal(false);
		setSelectedWorker(null);
	}

	var onWorkerUpdated = (worker: EventWorker)=>{
		setWorkers(prev => prev.map(w => w.worker_id === worker.worker_id ? worker : w));
		setShowAddWorkerModal(false);
		setSelectedWorker(null);
	}

	var onWorkerRated = (updatedWorker: EventWorker)=>{
		setWorkers(prev => prev.map(w => w.worker_id === updatedWorker.worker_id ? updatedWorker : w));
		setShowRateWorkerModal(false);
		setSelectedWorker(null);
	}

	var cancelWorker = (setLoading: SetLoading)=>{
		setLoading(true);
		API.cancelEventWorker(props.dateId, selectedWorker.worker_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha eliminado el trabajador del evento');
			
			setWorkers(prev => prev.filter(w => w.worker_id !== selectedWorker.worker_id));
			setShowCancelModal(false);
			setSelectedWorker(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando el trabajador (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}
	
	return <>
		<Table fitted striped
			title='Trabajadores'
			headers={['ID', 'Nombre', 'Puesto', 'Sueldo', 'Raiting', 'RFC', <Icon name='user-check' />, <Icon name='wrench' />]}
			style={{ maxWidth: 800, margin: 'auto' }}
			button={hasAccess(UserAccess.EVENTS.ADD_EVENT_WORKERS) && (
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button text='Opciones' color='black' iconRight='caret-down' iconRightStyle={{ marginRight: 0, marginLeft: 5 }} size='tiny'/>
				)}>
					<Dropdown.Menu>
						<Dropdown.Item text='Agregar trabajador' icon='plus' onClick={onAddWorker} />
						{workers.some(w => w.phone && !w.confirmed) && (
							<Dropdown.Item
								text='Notificar'
								icon='comment'
								onClick={onSendSMSBulk}
							/>
						)}
					</Dropdown.Menu>
				</Dropdown>
			)}
			footer={
				(!!workers && workers.length >= PAGE_SIZE || page !== 0) && (
					<Table.Cell colSpan={8} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={workers.length>=PAGE_SIZE} />
						</div>
					</Table.Cell>
				)
			}
		>
			{loading ? (
				<Table.Row>
					<Table.Cell colSpan={8}>
						<Header loading text='Cargando datos...' />
					</Table.Cell>
				</Table.Row>
			) : workers.length !== 0 ? (
				workers.map(w => (
					<Table.Row
						key={w.worker_id}
						collapsingIndexes={[0, 2, 3, 4, 5, 6, 7, 8]}
						centeredIndexes={[4, 5, 6, 7]}
						data={[
							w.worker_id,
							w.worker_name,
							w.title_name ? w.title_name : 'Otro',
							'$' + addCommas(w.payout_amount),
							w.rating ? <Rating key={`rating-${w.worker_id}`} icon="star" rating={w.rating} maxRating={5} size="small" disabled /> : <Icon name='minus' color='gray'/>,
							w.rfc ? <Icon name='check' color={w.valid_rfc ? 'green' : 'orange'}/> : <Icon name='close' color='red'/>,
							w.confirmed
								? <Icon name='check' color='green' />
							: w.declined
								? <Icon name='ban' color='orange' />
								: <Icon name='close' color='red' />,
							<Dropdown direction='left' icon={(
								<div className="fr label" style={{ padding: '3px 10px' }}>
									<Icon name='ellipsis' style={{ margin: 0 }} />
								</div>
							)} className='icon'>
								<Dropdown.Menu>
									<Dropdown.Item as={Link} to={`/workers/${w.worker_id}`} target="_blank">
										<Icon name="eye" style={{ marginRight: 3 }} /> Ver
									</Dropdown.Item>
									{hasAccess(UserAccess.EVENTS.EDIT_EVENT_WORKERS) && (
										<Dropdown.Item onClick={() => onEditWorker(w)}>
											<Icon name="pen" style={{ marginRight: 3 }} /> Editar
										</Dropdown.Item>
									)}
									{hasAccess(UserAccess.EVENTS.EDIT_EVENT_WORKERS) && (
										<Dropdown.Item onClick={() => onRateWorker(w)}>
											<Icon name="star" style={{ marginRight: 3 }} /> Calificar
										</Dropdown.Item>
									)}
								{hasAccess(UserAccess.EVENTS.ADD_EVENT_WORKERS) && w.phone && !w.confirmed && !w.declined && (
									<Dropdown.Item onClick={() => onSendSMSToWorker(w)}>
										<Icon name="comment-sms" style={{ marginRight: 3 }} /> Notificar
									</Dropdown.Item>
								)}
								{hasAccess(UserAccess.EVENTS.REMOVE_EVENT_WORKERS) && (
										<Dropdown.Item onClick={() => onShowCancel(w)}>
											<Icon name="close" style={{ marginRight: 3 }} /> Eliminar
										</Dropdown.Item>
									)}
									<Dropdown.Item onClick={() => { setSelectedWorker(w); setShowInfoLogModal(true); }}>
										<Icon name="info" style={{ marginRight: 3 }} /> Ver Logs
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						]}
					/>
				))
			) : (
				<Table.Cell row className='empty' colSpan={9}>
					No se encontraron registros
				</Table.Cell>
			)}
			{workers.length > 0 && <Table.Row>
				<Table.Cell colSpan={3} style={{ textAlign: 'right', fontSize: 14, fontWeight: 'bold' }}>
					Total:
				</Table.Cell>
				<Table.Cell collapsing colSpan={1} style={{ textAlign: 'start', fontSize: 14, fontWeight: 'bold' }}>
					${addCommas(workers.reduce((sum, w) => sum + parseFloat(w.payout_amount.toString()), 0))}
				</Table.Cell>
				<Table.Cell colSpan={1} style={{ textAlign: 'right', fontSize: 14, fontWeight: 'bold' }}>
					RFC:
				</Table.Cell>
				<Table.Cell collapsing colSpan={3} style={{ textAlign: 'start', fontSize: 14, fontWeight: 'bold', color: workers.some(w=>!w.rfc) ?  'brown' : workers.some(w=>!w.valid_rfc) ? 'orange' : 'green' }}>
					{workers.reduce((count, w) => w.rfc ? count + 1 : count, 0)}/{workers.length}
				</Table.Cell>
			</Table.Row>}
		</Table>
		<Modal open={showCancelModal} onClose={bindClick(setShowCancelModal, false)} size='tiny'>
			<Modal.Header>¿Eliminar trabajador?</Modal.Header>
			{selectedWorker ? <Modal.Content>
				<Header text='¿Eliminar trabajador de evento?'/>
				<Table 
					details
					striped
					data={[
						['Nombre', selectedWorker.worker_name],
						['Puesto', selectedWorker.title_name || 'Otro'],
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content> : null}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShowCancelModal, false)} />
				<Button text='Eliminar' color='red' onClick={cancelWorker}/>
			</Modal.Actions>
		</Modal>		
		<AddWorkerModal
			open={showAddWorkerModal}
			onClose={() => { setSelectedWorker(null); setShowAddWorkerModal(false) }}
			date_id={props.dateId}
			onAdded={onWorkerAdded}
			onUpdated={onWorkerUpdated}
			workerToEdit={selectedWorker}
		/>
		<RateWorkerModal
			open={showRateWorkerModal}
			onClose={() => { setSelectedWorker(null); setShowRateWorkerModal(false) }}
			date_id={props.dateId}
			worker={selectedWorker}
			onRated={onWorkerRated}
		/>
		<InfoLog
			open={showInfoLogModal}
			onClose={() => setShowInfoLogModal(false)}
			data={selectedWorker}
			title="Historial del Trabajador"
		/>
		<SendWorkersSMSModal
			open={showSMSModal}
			onClose={() => setShowSMSModal(false)}
			dateId={props.dateId}
			workers={smsSingleWorker ? [smsSingleWorker] : workers.filter(w => w.phone && !w.confirmed)}
			onSent={getWorkers}
		/>
	</>
};

export default WorkersTable;
